import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  BookOpen, Headphones, Play, ExternalLink, Lock, Crown, 
  Sparkles, Download, Clock, User, Star, BookMarked, Video,
  ShoppingBag, ArrowRight, CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Curated book catalog - Free audiobooks and PDFs (Public Domain - Archive.org, LibriVox, Project Gutenberg)
const bookCatalog = [
  {
    id: 'think-grow-rich',
    title: 'Think and Grow Rich',
    author: 'Napoleon Hill',
    category: 'Wealth Mindset',
    description: 'The timeless classic on the philosophy of personal achievement and the thirteen steps to riches.',
    year: 1937,
    audioUrl: 'https://archive.org/details/audiobook-thinkandgrowrich',
    pdfUrl: 'https://marjankogelnik.files.wordpress.com/2016/02/napoleon-hill-think-and-grow-rich.pdf',
    coverImage: 'https://images.pexels.com/photos/5726706/pexels-photo-5726706.jpeg',
    duration: '9 hrs',
    featured: true
  },
  {
    id: 'richest-man',
    title: 'The Richest Man in Babylon',
    author: 'George S. Clason',
    category: 'Financial Wisdom',
    description: 'Ancient Babylonian parables offering timeless financial wisdom for building wealth and keeping it.',
    year: 1926,
    audioUrl: 'https://archive.org/details/richestman_2604_librivox',
    pdfUrl: 'https://archive.org/download/RichestManInBabylon_650/the_richest_man_in_babylon.pdf',
    coverImage: 'https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg',
    duration: '4 hrs',
    featured: true
  },
  {
    id: 'art-of-war',
    title: 'The Art of War',
    author: 'Sun Tzu',
    category: 'Strategy & Power',
    description: 'Ancient military treatise with profound applications in business strategy and competitive positioning.',
    year: -500,
    audioUrl: 'https://archive.org/details/art_of_war_librivox',
    pdfUrl: 'https://www.gutenberg.org/files/132/132-h/132-h.htm',
    coverImage: 'https://images.pexels.com/photos/6147276/pexels-photo-6147276.jpeg',
    duration: '1.5 hrs',
    featured: true
  },
  {
    id: 'as-man-thinketh',
    title: 'As a Man Thinketh',
    author: 'James Allen',
    category: 'Psychology',
    description: 'A literary essay on the power of thought and its effect on character, circumstance, and destiny.',
    year: 1903,
    audioUrl: 'https://archive.org/details/as_a_man_thinketh_1312_librivox',
    pdfUrl: 'https://archive.org/download/asmanthinketh00alleiala/asmanthinketh00alleiala.pdf',
    coverImage: 'https://images.pexels.com/photos/3771074/pexels-photo-3771074.jpeg',
    duration: '54 min',
    featured: false
  },
  {
    id: 'science-getting-rich',
    title: 'The Science of Getting Rich',
    author: 'Wallace D. Wattles',
    category: 'Wealth Mindset',
    description: 'A practical guide to acquiring wealth through the power of thought and definite purpose.',
    year: 1910,
    audioUrl: 'https://archive.org/details/science_gettingrich_1005_librivox',
    pdfUrl: 'https://archive.org/download/TheScienceOfGettingRich.pdf/the_science_of_getting_rich.pdf',
    coverImage: 'https://images.pexels.com/photos/4386442/pexels-photo-4386442.jpeg',
    duration: '2 hrs',
    featured: false
  },
  {
    id: 'meditations',
    title: 'Meditations',
    author: 'Marcus Aurelius',
    category: 'Philosophy',
    description: 'Personal writings of the Roman Emperor on Stoic philosophy, self-discipline, and leadership.',
    year: 180,
    audioUrl: 'https://archive.org/details/themeditationsofmarcusaurelius_1801_librivox',
    pdfUrl: 'https://archive.org/download/themeditationsofmarcusaurelius_1801_librivox/MeditationsAnton_1712.pdf',
    coverImage: 'https://images.pexels.com/photos/6147369/pexels-photo-6147369.jpeg',
    duration: '5 hrs',
    featured: true
  },
  {
    id: 'power-subconscious',
    title: 'The Power of Your Subconscious Mind',
    author: 'Joseph Murphy',
    category: 'Psychology',
    description: 'Unlock the extraordinary potential of your subconscious mind to achieve your goals.',
    year: 1963,
    audioUrl: 'https://archive.org/details/the-power-of-your-subconscious-mind_202104',
    pdfUrl: 'https://archive.org/details/the-power-of-your-subconscious-mind_202104',
    coverImage: 'https://images.pexels.com/photos/3771097/pexels-photo-3771097.jpeg',
    duration: '6 hrs',
    featured: false
  },
  {
    id: 'autobiography-franklin',
    title: 'The Autobiography of Benjamin Franklin',
    author: 'Benjamin Franklin',
    category: 'Legacy & Influence',
    description: 'The life story of America\'s most successful polymath and his principles for achievement.',
    year: 1791,
    audioUrl: 'https://archive.org/details/autobiography_ben_franklin_librivox',
    pdfUrl: 'https://www.gutenberg.org/files/20203/20203-h/20203-h.htm',
    coverImage: 'https://images.pexels.com/photos/6147381/pexels-photo-6147381.jpeg',
    duration: '7 hrs',
    featured: false
  },
  {
    id: 'prince',
    title: 'The Prince',
    author: 'Niccolò Machiavelli',
    category: 'Strategy & Power',
    description: 'The controversial treatise on political power, leadership, and the nature of authority.',
    year: 1532,
    audioUrl: 'https://archive.org/details/prince_1104_librivox',
    pdfUrl: 'https://www.gutenberg.org/files/1232/1232-h/1232-h.htm',
    coverImage: 'https://images.pexels.com/photos/6147209/pexels-photo-6147209.jpeg',
    duration: '3 hrs',
    featured: true
  },
  {
    id: 'art-money-getting',
    title: 'The Art of Money Getting',
    author: 'P.T. Barnum',
    category: 'Financial Wisdom',
    description: 'Twenty golden rules for making money from the greatest showman and one of America\'s first millionaires.',
    year: 1880,
    audioUrl: 'https://archive.org/details/art_of_money_getting_librivox',
    pdfUrl: 'https://archive.org/download/LibrivoxCdCoverArt22/Art_Money_Getting_1210.pdf',
    coverImage: 'https://images.unsplash.com/photo-1755369394394-685cd2ea3140?w=400&h=300&fit=crop',
    duration: '1 hr',
    featured: true
  },
  {
    id: 'way-to-wealth',
    title: 'The Way to Wealth',
    author: 'Benjamin Franklin',
    category: 'Financial Wisdom',
    description: 'Franklin\'s most famous work on personal finance — a collection of advice on frugality, industry, and wealth building.',
    year: 1758,
    audioUrl: 'https://archive.org/details/nonfiction040_1601_librivox',
    pdfUrl: 'https://www.gutenberg.org/ebooks/10445',
    coverImage: 'https://images.unsplash.com/photo-1755369355222-8146801ccf90?w=400&h=300&fit=crop',
    duration: '25 min',
    featured: false
  },
  {
    id: 'acres-of-diamonds',
    title: 'Acres of Diamonds',
    author: 'Russell Conwell',
    category: 'Wealth Mindset',
    description: 'The legendary speech delivered over 6,000 times — your acres of diamonds are right where you stand.',
    year: 1890,
    audioUrl: 'https://archive.org/details/acres_of_diamonds_1008_librivox',
    pdfUrl: 'https://www.gutenberg.org/files/368/368-h/368-h.htm',
    coverImage: 'https://images.pexels.com/photos/5412270/pexels-photo-5412270.jpeg',
    duration: '1.5 hrs',
    featured: false
  },
  {
    id: 'self-reliance',
    title: 'Self-Reliance',
    author: 'Ralph Waldo Emerson',
    category: 'Philosophy',
    description: 'Emerson\'s defining essay on individualism, independent thinking, and trusting your own judgment above all else.',
    year: 1841,
    audioUrl: 'https://archive.org/details/essays_first_series_0708_librivox',
    pdfUrl: 'https://www.gutenberg.org/files/16643/16643-h/16643-h.htm',
    coverImage: 'https://images.pexels.com/photos/3771074/pexels-photo-3771074.jpeg',
    duration: '50 min',
    featured: false
  },
  {
    id: 'kybalion',
    title: 'The Kybalion',
    author: 'Three Initiates',
    category: 'Philosophy',
    description: 'The seven Hermetic principles — the master key to understanding the laws governing wealth, power, and success.',
    year: 1908,
    audioUrl: 'https://archive.org/details/kybalion_ghs_librivox',
    pdfUrl: 'https://www.gutenberg.org/ebooks/14209',
    coverImage: 'https://images.pexels.com/photos/6147276/pexels-photo-6147276.jpeg',
    duration: '3 hrs',
    featured: false
  },
  {
    id: 'common-sense',
    title: 'Common Sense',
    author: 'Thomas Paine',
    category: 'Legacy & Influence',
    description: 'The pamphlet that ignited a revolution — on independence, self-governance, and building something from nothing.',
    year: 1776,
    audioUrl: 'https://archive.org/details/commonsense_gg_librivox',
    pdfUrl: 'https://www.gutenberg.org/files/147/147-h/147-h.htm',
    coverImage: 'https://images.unsplash.com/photo-1755369346546-a33a261cac38?w=400&h=300&fit=crop',
    duration: '1.5 hrs',
    featured: false
  },
  {
    id: 'wealth-of-nations',
    title: 'The Wealth of Nations (Book 1)',
    author: 'Adam Smith',
    category: 'Financial Wisdom',
    description: 'The foundation of modern economics — understand markets, labor, capital, and the invisible hand that governs wealth.',
    year: 1776,
    audioUrl: 'https://archive.org/details/wealth_nations01_se',
    pdfUrl: 'https://www.gutenberg.org/files/3300/3300-h/3300-h.htm',
    coverImage: 'https://images.unsplash.com/photo-1657901326739-d41a9723c2b8?w=400&h=300&fit=crop',
    duration: '9 hrs',
    featured: true
  }
];

// Curated YouTube videos
const videoCollection = [
  {
    id: 'v1',
    title: 'How The Economic Machine Works',
    channel: 'Ray Dalio',
    description: 'Ray Dalio explains how the economy works in 30 minutes.',
    thumbnail: 'https://img.youtube.com/vi/PHe0bXAIuk0/maxresdefault.jpg',
    videoId: 'PHe0bXAIuk0',
    duration: '31 min',
    category: 'Economics'
  },
  {
    id: 'v2',
    title: 'Principles for Success',
    channel: 'Ray Dalio',
    description: 'An animated series based on Ray Dalio\'s book Principles.',
    thumbnail: 'https://img.youtube.com/vi/B9XGUpQZY38/maxresdefault.jpg',
    videoId: 'B9XGUpQZY38',
    duration: '27 min',
    category: 'Success Mindset'
  },
  {
    id: 'v3',
    title: 'The Psychology of Money',
    channel: 'The Swedish Investor',
    description: 'Key lessons from Morgan Housel\'s bestselling book.',
    thumbnail: 'https://img.youtube.com/vi/TJDcGv9OH4Q/maxresdefault.jpg',
    videoId: 'TJDcGv9OH4Q',
    duration: '18 min',
    category: 'Wealth Mindset'
  },
  {
    id: 'v4',
    title: 'Rich Dad Poor Dad Summary',
    channel: 'Practical Wisdom',
    description: 'Robert Kiyosaki\'s key principles for financial freedom.',
    thumbnail: 'https://img.youtube.com/vi/TcNpoc-lF0M/maxresdefault.jpg',
    videoId: 'TcNpoc-lF0M',
    duration: '12 min',
    category: 'Financial Education'
  },
  {
    id: 'v5',
    title: 'The 48 Laws of Power',
    channel: 'Illacertus',
    description: 'Animated summary of Robert Greene\'s controversial masterpiece.',
    thumbnail: 'https://img.youtube.com/vi/670ZGMBjrPI/maxresdefault.jpg',
    videoId: '670ZGMBjrPI',
    duration: '45 min',
    category: 'Strategy & Power'
  },
  {
    id: 'v6',
    title: 'Atomic Habits Summary',
    channel: 'Productivity Game',
    description: 'James Clear\'s framework for building good habits.',
    thumbnail: 'https://img.youtube.com/vi/PZ7lDrwYdZc/maxresdefault.jpg',
    videoId: 'PZ7lDrwYdZc',
    duration: '10 min',
    category: 'Personal Development'
  }
];

const categories = ['All', 'Wealth Mindset', 'Strategy & Power', 'Psychology', 'Philosophy', 'Financial Wisdom', 'Legacy & Influence'];

export default function GildedStudy() {
  const [activeTab, setActiveTab] = useState('products');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  
  const filteredBooks = selectedCategory === 'All' 
    ? bookCatalog 
    : bookCatalog.filter(book => book.category === selectedCategory);
  
  const featuredBooks = bookCatalog.filter(book => book.featured);
  
  const openBook = (book) => {
    setSelectedBook(book);
    setBookModalOpen(true);
  };
  
  const openVideo = (video) => {
    setSelectedVideo(video);
    setVideoModalOpen(true);
  };
  
  return (
    <div className="min-h-screen bg-[#0d0c0a] pt-20" data-testid="gilded-study">
      {/* Hero Section - Exclusive & Elegant */}
      <section className="relative min-h-[70vh] overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: 'url(https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0c0a] via-[#0d0c0a]/80 to-[#0d0c0a]" />
          {/* Elegant pattern overlay */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          {/* Crown Icon */}
          <div className="inline-flex items-center justify-center p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-8">
            <Crown className="h-10 w-10 text-[#D4AF37]" />
          </div>
          
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FAFAFA] mb-6">
            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F5E6A3]">Gilded</span> Study
          </h1>
          
          <p className="font-body text-xl text-[#FAFAFA]/70 max-w-3xl mx-auto mb-8 leading-relaxed">
            An invitation-only literary society established by Nouveau Riche Legacy. A private space for 
            intentional reading, elevated discussion, and the cultivation of legacy-level thinking.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2">
              <Lock className="h-3 w-3 mr-2" />
              Exclusive Access
            </Badge>
            <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2">
              <BookMarked className="h-3 w-3 mr-2" />
              Curated Collection
            </Badge>
            <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2">
              <Sparkles className="h-3 w-3 mr-2" />
              Wealth Consciousness
            </Badge>
          </div>
          
          {/* Philosophy Statement */}
          <div className="max-w-2xl mx-auto p-8 border border-[#D4AF37]/20 bg-[#1c1814]/50 backdrop-blur-sm">
            <p className="text-[#FAFAFA]/60 italic leading-relaxed">
              "The focus is not speed or volume, but depth, discernment, and application. 
              This is not a book club. It is a study in refinement, strategy, and long-term wealth consciousness."
            </p>
          </div>
        </div>
      </section>
      
      {/* Navigation Tabs */}
      <section className="sticky top-20 z-40 bg-[#0d0c0a]/95 backdrop-blur-md border-y border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent gap-4">
              <TabsTrigger 
                value="products"
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814] bg-[#1c1814] text-[#FAFAFA] border border-[#D4AF37]/20 px-6 py-3"
                data-testid="tab-products"
              >
                <ShoppingBag className="h-4 w-4 mr-2" />
                NRL Products
              </TabsTrigger>
              <TabsTrigger 
                value="library"
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814] bg-[#1c1814] text-[#FAFAFA] border border-[#D4AF37]/20 px-6 py-3"
                data-testid="tab-library"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                The Library
              </TabsTrigger>
              <TabsTrigger 
                value="audiobooks"
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814] bg-[#1c1814] text-[#FAFAFA] border border-[#D4AF37]/20 px-6 py-3"
                data-testid="tab-audiobooks"
              >
                <Headphones className="h-4 w-4 mr-2" />
                Audiobooks
              </TabsTrigger>
              <TabsTrigger 
                value="videos"
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814] bg-[#1c1814] text-[#FAFAFA] border border-[#D4AF37]/20 px-6 py-3"
                data-testid="tab-videos"
              >
                <Video className="h-4 w-4 mr-2" />
                Video Studies
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </section>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Library Tab - Books Catalog */}
        {activeTab === 'library' && (
          <div data-testid="library-section">
            {/* Featured Collection */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <Star className="h-6 w-6 text-[#D4AF37]" />
                <h2 className="font-display text-3xl text-[#FAFAFA]">Essential Reading</h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredBooks.map((book) => (
                  <Card 
                    key={book.id}
                    className="bg-[#1c1814] border-[#D4AF37]/30 hover:border-[#D4AF37] overflow-hidden cursor-pointer transition-all group"
                    onClick={() => openBook(book)}
                    data-testid={`book-${book.id}`}
                  >
                    <div className="h-48 overflow-hidden relative">
                      <img 
                        src={book.coverImage}
                        alt={book.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1c1814] to-transparent" />
                      <Badge className="absolute top-3 right-3 bg-[#D4AF37] text-[#1c1814]">
                        <Star className="h-3 w-3 mr-1" />
                        Essential
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <Badge variant="outline" className="mb-3 border-[#D4AF37]/30 text-[#D4AF37]">
                        {book.category}
                      </Badge>
                      <h3 className="font-display text-xl text-[#FAFAFA] mb-1">{book.title}</h3>
                      <p className="text-[#D4AF37] text-sm mb-3">{book.author}</p>
                      <p className="text-[#FAFAFA]/60 text-sm line-clamp-2">{book.description}</p>
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#D4AF37]/10">
                        {book.audioUrl && (
                          <span className="text-xs text-[#FAFAFA]/50 flex items-center">
                            <Headphones className="h-3 w-3 mr-1" />
                            {book.duration}
                          </span>
                        )}
                        {book.pdfUrl && (
                          <span className="text-xs text-[#FAFAFA]/50 flex items-center">
                            <Download className="h-3 w-3 mr-1" />
                            PDF
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className={selectedCategory === cat 
                      ? 'bg-[#D4AF37] text-[#1c1814]' 
                      : 'border-[#D4AF37]/30 text-[#FAFAFA] hover:bg-[#D4AF37]/10'
                    }
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Full Catalog */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredBooks.map((book) => (
                <Card 
                  key={book.id}
                  className="bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 overflow-hidden cursor-pointer transition-all group"
                  onClick={() => openBook(book)}
                >
                  <div className="h-40 overflow-hidden relative">
                    <img 
                      src={book.coverImage}
                      alt={book.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1c1814] to-transparent" />
                  </div>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#D4AF37] mb-1">{book.category}</p>
                    <h3 className="font-display text-lg text-[#FAFAFA] line-clamp-1">{book.title}</h3>
                    <p className="text-[#FAFAFA]/50 text-sm">{book.author}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Audiobooks Tab */}
        {activeTab === 'audiobooks' && (
          <div data-testid="audiobooks-section">
            <div className="mb-8">
              <h2 className="font-display text-3xl text-[#FAFAFA] mb-2">Free Audiobook Collection</h2>
              <p className="text-[#FAFAFA]/60">Listen to timeless wisdom on wealth, strategy, and personal mastery.</p>
            </div>
            
            <div className="space-y-4">
              {bookCatalog.filter(b => b.audioUrl).map((book) => (
                <Card 
                  key={book.id}
                  className="bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={book.coverImage}
                          alt={book.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-2 border-[#D4AF37]/30 text-[#D4AF37] text-xs">
                          {book.category}
                        </Badge>
                        <h3 className="font-display text-lg text-[#FAFAFA]">{book.title}</h3>
                        <p className="text-[#FAFAFA]/50 text-sm">{book.author}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3 text-[#FAFAFA]/40" />
                          <span className="text-xs text-[#FAFAFA]/40">{book.duration}</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => window.open(book.audioUrl, '_blank')}
                        className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90"
                      >
                        <Headphones className="h-4 w-4 mr-2" />
                        Listen Free
                      </Button>
                      {book.pdfUrl && (
                        <Button
                          onClick={() => window.open(book.pdfUrl, '_blank')}
                          variant="outline"
                          className="border-[#D4AF37]/30 text-[#FAFAFA] hover:bg-[#D4AF37]/10"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div data-testid="videos-section">
            <div className="mb-8">
              <h2 className="font-display text-3xl text-[#FAFAFA] mb-2">Video Studies</h2>
              <p className="text-[#FAFAFA]/60">Curated video content for visual learners seeking wealth consciousness.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videoCollection.map((video) => (
                <Card 
                  key={video.id}
                  className="bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 overflow-hidden cursor-pointer transition-all group"
                  onClick={() => openVideo(video)}
                  data-testid={`video-${video.id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-[#0d0c0a]/40 flex items-center justify-center">
                      <div className="p-4 bg-[#D4AF37] rounded-full group-hover:scale-110 transition-transform">
                        <Play className="h-8 w-8 text-[#1c1814] fill-current" />
                      </div>
                    </div>
                    <Badge className="absolute top-3 left-3 bg-[#1c1814]/80 text-[#FAFAFA]">
                      {video.duration}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <Badge variant="outline" className="mb-2 border-[#D4AF37]/30 text-[#D4AF37] text-xs">
                      {video.category}
                    </Badge>
                    <h3 className="font-display text-lg text-[#FAFAFA] line-clamp-2">{video.title}</h3>
                    <p className="text-[#FAFAFA]/50 text-sm mt-1">{video.channel}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* NRL Products Tab */}
        {activeTab === 'products' && (
          <div data-testid="products-section">
            <div className="mb-12">
              <h2 className="font-display text-3xl text-[#FAFAFA] mb-2">NRL Products</h2>
              <p className="text-[#FAFAFA]/60">Original publications and resources by Nouveau Riche Legacy.</p>
            </div>

            {/* The Wealth Lexicon — Featured Product */}
            <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-6 bg-[#D4AF37]/10 blur-3xl rounded-full" />
                  <img
                    src="https://customer-assets.emergentagent.com/job_3a854a85-d099-4060-868a-517959e97093/artifacts/c7l5gfu4_7289C4F8-554B-401A-9251-05B028DA18D5.png"
                    alt="The Wealth Lexicon"
                    className="relative z-10 w-64 sm:w-72 rounded-lg shadow-2xl shadow-[#D4AF37]/20"
                    data-testid="gilded-book-cover"
                  />
                </div>
              </div>
              <div>
                <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 mb-4">
                  <Sparkles className="h-3 w-3 mr-1" /> Pre-Order Available
                </Badge>
                <h3 className="font-display text-3xl text-[#FAFAFA] mb-2">The Wealth Lexicon</h3>
                <p className="text-[#D4AF37] text-lg mb-4 font-display italic">Don't Let Them Speak Circles Around You</p>
                <p className="text-[#FAFAFA]/60 mb-6 leading-relaxed">
                  Your essential guide to financial literacy and building generational wealth.
                  From credit building to investing strategies, The Wealth Lexicon gives you the vocabulary 
                  and knowledge to command your financial future.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    { label: "eBook", price: "$39" },
                    { label: "Softback", price: "$75" },
                    { label: "Hardback", price: "$150" },
                    { label: "VIP Bundle", price: "$250" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#0d0c0a] border border-[#D4AF37]/20 rounded-lg px-4 py-3">
                      <span className="text-[#FAFAFA]/70 text-sm">{item.label}</span>
                      <span className="text-[#D4AF37] font-bold">{item.price}</span>
                    </div>
                  ))}
                </div>

                <ul className="space-y-2 mb-8">
                  {["Financial literacy vocabulary", "Market vocabulary & understanding", "Peace of mind over your finances", "Access to larger money conversations"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#FAFAFA]/70">
                      <CheckCircle className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link to="/the-wealth-lexicon">
                  <Button className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90 px-8 py-5 text-base" data-testid="gilded-preorder-cta">
                    <BookMarked className="h-4 w-4 mr-2" /> Pre-Order Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Closing Statement */}
      <section className="py-20 border-t border-[#D4AF37]/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Crown className="h-10 w-10 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="font-display text-3xl text-[#FAFAFA] mb-4">
            Access is Limited. Participation is Guided.
          </h2>
          <p className="text-[#FAFAFA]/60 leading-relaxed">
            Discussions are confidential. Participation is guided by discretion, intellectual curiosity, 
            and respect for the room. The Gilded Study exists for those committed to the long game of 
            wealth consciousness and legacy building.
          </p>
        </div>
      </section>
      
      {/* Book Detail Modal */}
      <Dialog open={bookModalOpen} onOpenChange={setBookModalOpen}>
        <DialogContent className="bg-[#1c1814] border-[#D4AF37]/20 max-w-2xl">
          {selectedBook && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-6">
                  <div className="w-32 h-44 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={selectedBook.coverImage}
                      alt={selectedBook.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <Badge variant="outline" className="mb-2 border-[#D4AF37]/30 text-[#D4AF37]">
                      {selectedBook.category}
                    </Badge>
                    <DialogTitle className="font-display text-2xl text-[#FAFAFA]">
                      {selectedBook.title}
                    </DialogTitle>
                    <p className="text-[#D4AF37] mt-1">{selectedBook.author}</p>
                    <p className="text-[#FAFAFA]/50 text-sm mt-1">
                      First published {selectedBook.year > 0 ? selectedBook.year : `${Math.abs(selectedBook.year)} BC`}
                    </p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-[#FAFAFA]/70 leading-relaxed">
                  {selectedBook.description}
                </p>
              </div>
              
              <div className="flex gap-4 pt-4 border-t border-[#D4AF37]/20">
                {selectedBook.audioUrl && (
                  <Button
                    onClick={() => window.open(selectedBook.audioUrl, '_blank')}
                    className="flex-1 bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90"
                  >
                    <Headphones className="h-4 w-4 mr-2" />
                    Listen Free ({selectedBook.duration})
                  </Button>
                )}
                {selectedBook.pdfUrl && (
                  <Button
                    onClick={() => window.open(selectedBook.pdfUrl, '_blank')}
                    variant="outline"
                    className="flex-1 border-[#D4AF37]/30 text-[#FAFAFA] hover:bg-[#D4AF37]/10"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Read PDF
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Video Modal */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="bg-[#1c1814] border-[#D4AF37]/20 max-w-4xl p-0">
          {selectedVideo && (
            <>
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded-t-lg"
                />
              </div>
              <div className="p-6">
                <Badge variant="outline" className="mb-2 border-[#D4AF37]/30 text-[#D4AF37]">
                  {selectedVideo.category}
                </Badge>
                <h3 className="font-display text-xl text-[#FAFAFA]">{selectedVideo.title}</h3>
                <p className="text-[#D4AF37] text-sm mt-1">{selectedVideo.channel}</p>
                <p className="text-[#FAFAFA]/60 text-sm mt-2">{selectedVideo.description}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
